import React from "react";
import OnePanel from "./OnePanel";
import Styled, { LineBreak, Exhibit } from "./styles";
import CanvasImage from "./assets/canvas.png";
import { minBy, maxBy } from "lodash";
import styledComponents from 'styled-components';

const Name = styledComponents.span`
  color: #ff5252;
  font-weight: bold;
`;

const calculate1 = initialDemand =>
  ((initialDemand.auto + initialDemand.mobile) / 1000000).toFixed(2);
const calculate2 = salesClients => {
  const salesRep = salesClients.find(i => i.name === "SalesRep");

  return salesRep ? salesRep.commission_rate * 100 : 0;
};
const calculate3 = customerPreferences => {
  return minBy(customerPreferences, i => i.min_price).min_price;
};
const calculate4 = customerPreferences => {
  return maxBy(customerPreferences, i => i.max_price).max_price;
};

const Market = ({
  initialDemand,
  salesClients,
  customerPreferences,
  activeRound
}) => (
  <OnePanel title="THE MARKET">
    Even though William was not optimistic about the image sensor opportunity, he felt obliged to make an informed decision on behalf of his team,
    rather than simply rejecting it. Therefore he reached out to his cousin Emily, who is a senior equity research analyst at XYZ Investments. Without
    even realizing that Emily’s specialization is in the semiconductor industry, he asked her about the market potential of the image sensors.
    <LineBreak />
    <Name>Emily</Name>: "I love this sector! An image sensor is such a tiny product, but the applications are enormous! Smartphone developers are the main buyers,
    but exponential growth is expected in the automobile, healthcare, and surveillance markets. Gross margins in the mobile segment are roughly ~35%,
    but the auto segment can be roughly 2x that. Why are you interested in image sensors?"
    <LineBreak />
    <Name>William</Name>: "You know that my buddies and I are interested in a start-up gig, right? One of my buddies read this article and became interested in making
    image sensors. Honestly I know nothing about them, and I wasn’t interested."
    <LineBreak />
    <Name>Emily</Name>: "There are major global players in healthcare and surveillance spaces and it will be hard for you to compete there. However from my analysis, a
    substantial unmet demand still exists in mobile phone and automobile sectors; since there aren’t large dominant players your chances would be better in those
    two segments. Based on my calculations, there will be about ~{calculate1(initialDemand)} million units of unmet demand next year from the mobile and auto markets.
    Here is some market information on the unmet demand and growth projections for the image sensor needs in the mobile and auto market segments
    (<Exhibit number={1} round={activeRound} />)."
    <LineBreak />
    At the end of the conversation, William’s thoughts changed from pessimistic to cautiously optimistic. He was impatient to share this new information with his team.
    <LineBreak />
    Anna continued researching the image sensor market. The more she read, the more optimistic she became. While Bob got discouraged after his call with Tom,
    Anna and William convinced him to join a team meeting to discuss the image sensor opportunity further. William was able to convince Jose, his freshman year
    roommate, a materials science major who is now working for a silicon chip manufacturer, to join  Team Z. Based on the initial chit-chat, it was clear to
    Anna that if the billion dollar facility cost that Tom mentioned is true, then there is no point in wasting time. Therefore, Anna asked Jose to share his
    thoughts on this.
    <LineBreak />
    <Name>Jose</Name>: "It will not be realistic for you to build a manufacturing facility from scratch. However, there are few companies that operated semiconductor fabs
    in multiple locations but ceased operations of some when they consolidated their operations. Those fabs already have the cleanrooms and other infrastructure
    that is needed.  All you will have to do may be to lease a fab and buy the machines for the production lines. So, you will be looking at about one million
    dollars lease and another few millions for the production line costs. In other words, you will be looking at a few millions, instead of a billion."
    <LineBreak />
    With that, Anna noticed her team lightening up. She took a quick vote to see if the team wants to pursue the opportunity further. With everyone on board,
    Anna started talking about the next steps.
    <LineBreak />
    <Name>Anna</Name>: "Let’s start by drafting our business plan."
    <LineBreak />
    <Name>William</Name>: "You mean now? Doesn’t it take days? Isn’t it a comprehensive document?"
    <LineBreak />
    <Name>Anna</Name>: "Yes, a traditional business plan is, but we can use the BMC framework."
    <LineBreak />
    <Name>William</Name>: "What’s BMC?"
    <LineBreak />
    <Name>Bob</Name>: "It stands for Business Model Canvas. It is a lean business plan. BMC has a high-level focus and contains only the key factors."
    <LineBreak />
    Teresa did a quick search on her tablet.
    <LineBreak />
    <Name>Teresa</Name>: "A typical BMC is a single page document. BMC framework is based on nine  factors. See here:"
    <LineBreak />
    <Styled.ResponsiveImage src={CanvasImage} alt="canvas" />
    <LineBreak />
    <Name>Anna</Name>: "Okay, let’s take a look at these nine elements. Based on what Jose said, we might be able to secure a key partner on the fab facility side."
    <LineBreak />
    <Name>Bob</Name>: "Wait Anna, I think our starting point should be the customers. If we don’t have customers we don’t have anything."
    <LineBreak />
    <Name>Teresa</Name>: "I agree, let’s start with the customer segments, value propositions. After that we will look at the other elements."
    <LineBreak />
    <Name>Anna</Name>: "Any suggestions on how we can get more information about customers?"
    <LineBreak />
    <Name>Jose</Name>: "Trade shows! All the players will be there; not just customers, all players.. You guys should attend the CES conference and tradeshow!
    It’s coming soon! It’s the most influential tech event in the world. There are about 170,000 people that attend each year. This includes customers
    who demand image sensors (e.g. Apple, Tesla, Ford, and many other OEMs)."
    <LineBreak />
    As Jose was speaking, Bob did a quick search, landed on the Consumer Technology Association, the organizer of the CES. He read outloud.
    <LineBreak />
    <Name>Bob</Name>: "CES is the global stage for innovation. This is where the world's biggest brands do business and meet new partners, and the sharpest innovators hit the stage"
    <LineBreak />
    <Name>Anna</Name>: "Sounds like a plan! If that’s not a great place for customer discovery, I’m not sure what is."
    <LineBreak />
    <Styled.Title>CES Conference</Styled.Title>
    The team agreed that Anna should attend <a href="http://www.ces.tech/Conference" target="_blank" rel="noreferrer">CES</a> held
    in Las Vegas. Determined to learn and network as much as possible Anna attended as many presentations and visited as many booths
    as possible. On the third day of the conference, while grabbing drinks at the hotel bar at the conference, Anna overheard a
    conversation around image sensors.. She decided to join the conversation:
    <LineBreak />
    <Name>Anna</Name>: "I’m sorry, but I couldn’t help but listen to you two complain about the image sensor market. What’s to complain about? Seems
    like it’s a great market to be in right now. Hi, my name is Anna."
    <LineBreak />
    <Name>Fred</Name>: "Hi I am Fred. I actually love the image sensor space. Our gripe is with corporate. There is so much more of the market we can
    capture, but corporate is not investing in capacity. This has impacted me personally because my compensation is heavily based on
    commissions. I earn {calculate2(salesClients)}% on all sales contracts."
    <LineBreak />
    <Name>Anna</Name>: "Can’t you join a company that is willing to expand? Is the {calculate2(salesClients)}% sales commission pretty standard for this industry?"
    <LineBreak />
    <Name>Fred</Name>: "I’ve looked into that, but all the other players either have capacity constraints or are focused on other customer segments.
    But, to answer your other question, yes {calculate2(salesClients)}% commission is almost like an industry standard for direct selling."
    <LineBreak />
    <Name>Anna</Name>: "Who are these customers that no one is serving right now?"
    <LineBreak />
    <Name>Fred</Name>: "Boy!!! You are asking a lot of questions. Well, I am planning to retire soon and I attended this year’s conference for fun
    and to say my goodbyes. I have no ambitions to pursue. So, you caught me at the perfect time to ask all these questions; So what
    was your last question?"
    <LineBreak />
    <Name>Anna</Name>: "I appreciate your willingness to answer my questions. Currently which customers have unmet demand?"
    <LineBreak />
    <Name>Fred</Name>: "These are mobile phone manufacturers and automobile manufacturers. In the image sensor world, we call them mobile customers
    and auto customers. Based on the information I have, there will be an unmet demand for {calculate1(initialDemand)}M sensors next year, just in these two
    market segments and all sales occur at year end of year (i.e. customers buy in December)."
    <LineBreak />
    <Name>Anna</Name>: "How much are these customers willing to pay?"
    <LineBreak />
    <Name>Fred</Name>: "The price they are willing to pay ranges from ${calculate3(customerPreferences)} to ${calculate4(customerPreferences)}
    &mdash; mobile customers in the lower end and the auto customers in the higher end. The lower and upper prices that these two customer segments
    are willing to pay are very different. For example, no mobile customer will pay over $15.00 for an image sensor because that would be too
    expensive for them. At the same time, any sensor priced at or below $1.00 is probably too good to be true (i.e. really bad chips) and
    therefore, they won’t buy. Auto customers on the other hand, will not pay more than $40 for an image sensor, and they believe that any
    sensor sold for less than $5, is too good to be true. The Customer Analysis Page has details on the minimum product features customers
    will accept and the prices that each customer is willing to pay."
    <LineBreak />
    <Name>Anna</Name>: "Oh wow!! This is a pretty detailed customer analysis; I like how you have identified the minimum product requirements of each customer."
    <LineBreak />
    <Name>Fred</Name>: "None of these customers are loyal. They will always buy the highest quality product at the lowest price. Actually let me rephrase,
    mobile customers are more price sensitive and auto customers are more sensitive about the image sensor’s quality."
    <LineBreak />
    <Name>Anna</Name>: "How do you reach these customers?"
    <LineBreak />
    <Name>Fred</Name>: "Trade shows!!! Right here, CES is one of the best. Jon may be the best person to explain about reaching customers."
    <LineBreak />
    <Name>Jon</Name>: "Thanks for volunteering with me Fred. Well, I am not planning to retire. However, since our company has customers to buy every
    single sensor that we are able to make, I don’t mind sharing my secrets with a newcomer to our industry."
    <LineBreak />
    <Name>Anna</Name>: "Well thank you. I really appreciate it."
    <LineBreak />
    <Name>Jon</Name>: "We do direct selling to our larger customers that we meet through trade shows. Trade shows are where the magic happens. When we
    spend somewhere between $3M to $4M annually on trade shows, we make the most impact on generating product awareness. We tend to spend
    slightly more for the autonomous vehicle segment given that it’s a bit newer. When we spend more than $4M in a given year, we aren't
    getting any additional value. It takes a few years of marketing to achieve the greatest market awareness."
    <LineBreak />
    <Name>Fred</Name>: "One last thing to note. It’s a pain to sell directly to smaller customers. Honestly, most of them don’t attend trade shows; so
    it is hard to connect with them directly. Therefore we tend to work with distributors to sell to customers with smaller demand. These
    tend to be the customers listed in our Customer Analysis Report with volume of 5% or less within a segment."
    <LineBreak />
    <Name>Jon</Name>: "We market to smaller customers through various online distribution channels. When we market online, we only need to spend a fraction
    of what we spend on trade shows, roughly one fifth."
    <LineBreak />
    Anna collected their business cards and quickly sent a message to the team summarizing the wealth of information she gathered. about potential
    partnerships and customers.
    <LineBreak />
    Based on the conversations she had with Fred and Jon, the importance of understanding the customers’ product requirements is a key determinant
    of the success or failure of an image sensor manufacturer. While she understood the product features, she was not sure if Bob and William did.
    She therefore summarized the product features as below.
  </OnePanel>
);

export default Market;


// WEBPACK FOOTER //
// ./src/components/bcase/Market.js